/**
 * Telecom v2 — deterministic helpers powering the redesigned
 * "Cyber Resilience & Functional Adequacy" executive section.
 *
 * Pure functions (no I/O, no AI). Same inputs → same outputs, so the
 * UI and PDF stay in lock-step.
 */

export type MaturityBand = "Minimal" | "Adequate" | "Strong" | "Extensive";

export const FUNCTION_ORDER = [
  "Govern",
  "Identify",
  "Protect",
  "Detect",
  "Respond",
  "Recover",
] as const;

export type FunctionName = typeof FUNCTION_ORDER[number];

export interface FunctionScoreRow {
  function_name: string;
  /** 0–2 scale from calculate_nist_function_scores RPC. */
  average_score: number | null | undefined;
  maturity_level?: string | null;
  controls_evaluated?: number | null;
}

export interface FindingRow {
  control_code?: string | null;
  finding_title?: string | null;
  risk_level?: string | null;
  /** Optional NIST function — when present we use it directly. */
  nist_function?: string | null;
}

/** Backend-aligned mapping (stricter consulting model — parity with CSV/NIST RPCs).
 *  Bands (0-2 reviewer scale):
 *    Minimal:   avg <  0.50
 *    Adequate:  0.50 <= avg <  1.50
 *    Strong:    1.50 <= avg <  1.85
 *    Extensive: avg >= 1.85
 */
export function bandFromAverage(avg: number | null | undefined, controlsEvaluated = 1): MaturityBand | "Unrated" {
  if (controlsEvaluated <= 0 || avg == null || Number.isNaN(Number(avg))) return "Unrated";
  const v = Number(avg);
  if (v < 0.5) return "Minimal";
  if (v < 1.5) return "Adequate";
  if (v < 1.85) return "Strong";
  return "Extensive";
}

const BAND_RANK: Record<MaturityBand, number> = {
  Minimal: 0,
  Adequate: 1,
  Strong: 2,
  Extensive: 3,
};

/** One-line executive summary phrased for board readers. */
export function overallSummaryLine(band: MaturityBand | "Unrated", orgName?: string | null): string {
  const subject = orgName?.trim() ? orgName.trim() : "The organisation";
  switch (band) {
    case "Extensive":
      return `${subject} demonstrates an extensive cyber-resilience posture, with mature security capabilities operating consistently across the business.`;
    case "Strong":
      return `${subject} maintains a strong cyber-resilience posture, with most core security capabilities operating effectively and only targeted improvements required.`;
    case "Adequate":
      return `${subject} has an adequate cyber-resilience posture; foundational security capabilities are in place but several areas require strengthening.`;
    case "Minimal":
      return `${subject} shows a minimal cyber-resilience posture, with material gaps across multiple security capabilities that warrant immediate executive attention.`;
    default:
      return `Reviewer scoring is incomplete; cyber-resilience posture cannot yet be interpreted.`;
  }
}

export interface FunctionVisualRow {
  fn: FunctionName;
  band: MaturityBand | "Unrated";
  /** 0–100 fill ratio derived from band only (no exposed numerics). */
  fill: number;
}

const BAND_FILL: Record<MaturityBand | "Unrated", number> = {
  Unrated: 8,
  Minimal: 25,
  Adequate: 50,
  Strong: 75,
  Extensive: 100,
};

/** Always returns six rows in canonical order. */
export function buildFunctionVisualRows(rows: FunctionScoreRow[]): FunctionVisualRow[] {
  const map = new Map<string, FunctionScoreRow>();
  rows.forEach((r) => map.set(String(r.function_name), r));
  return FUNCTION_ORDER.map((fn) => {
    const row = map.get(fn);
    const band = bandFromAverage(row?.average_score, Number(row?.controls_evaluated ?? 0));
    return { fn, band, fill: BAND_FILL[band] };
  });
}

/**
 * Deterministic "Priority Focus Areas" — 2–4 bullets keyed off the
 * lowest-rated functions, enriched with related findings.
 */
export function buildPriorityFocusAreas(
  rows: FunctionVisualRow[],
  findings: FindingRow[],
): string[] {
  // Rank rows by band (lowest first), excluding Unrated unless there is
  // nothing else to show. Stable order: Govern → Recover for ties.
  const rated = rows.filter((r) => r.band !== "Unrated") as Array<FunctionVisualRow & { band: MaturityBand }>;
  const sorted = [...rated].sort((a, b) => {
    const diff = BAND_RANK[a.band] - BAND_RANK[b.band];
    if (diff !== 0) return diff;
    return FUNCTION_ORDER.indexOf(a.fn) - FUNCTION_ORDER.indexOf(b.fn);
  });

  // Pick 2–4 weakest. Always include all Minimal/Adequate; cap at 4.
  const weak = sorted.filter((r) => r.band === "Minimal" || r.band === "Adequate");
  let picks = weak.length >= 2 ? weak.slice(0, 4) : sorted.slice(0, Math.min(4, Math.max(2, sorted.length)));
  if (picks.length === 0 && rows.length > 0) picks = rows.slice(0, 2) as any;

  // Build a finding lookup: for each row we want a representative finding.
  // We try (a) explicit nist_function match, (b) high/critical risk findings as fallback.
  const byFn = new Map<string, FindingRow[]>();
  findings.forEach((f) => {
    const fn = (f.nist_function ?? "").trim();
    if (!fn) return;
    const list = byFn.get(fn) ?? [];
    list.push(f);
    byFn.set(fn, list);
  });

  const highRisk = findings.filter(
    (f) => f.risk_level === "High" || f.risk_level === "Critical",
  );

  return picks.map((p) => {
    const verb = p.band === "Minimal" ? "Address material gaps in" : "Strengthen";
    const fnFindings = byFn.get(p.fn) ?? [];
    const candidate = fnFindings[0] ?? highRisk[0];
    const tail = candidate?.finding_title?.trim()
      ? ` — e.g. ${truncate(candidate.finding_title.trim(), 110)}`
      : ` — controls in this function are currently rated ${p.band}.`;
    return `${verb} ${p.fn}${tail}`;
  });
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1).trimEnd() + "…";
}

/** Color tokens used across UI + PDF for maturity bands. */
export const BAND_COLORS: Record<MaturityBand | "Unrated", { hex: string; rgb: [number, number, number]; bg: string; text: string }> = {
  Minimal:   { hex: "#DC2626", rgb: [220, 38, 38],  bg: "#FEE2E2", text: "#7F1D1D" },
  Adequate:  { hex: "#F59E0B", rgb: [245, 158, 11], bg: "#FEF3C7", text: "#78350F" },
  Strong:    { hex: "#16A34A", rgb: [22, 163, 74],  bg: "#DCFCE7", text: "#14532D" },
  Extensive: { hex: "#2563EB", rgb: [37, 99, 235],  bg: "#DBEAFE", text: "#1E3A8A" },
  Unrated:   { hex: "#9CA3AF", rgb: [156, 163, 175],bg: "#F3F4F6", text: "#374151" },
};
